/**
 * AdminEscapadas — list + inline editor for editorial escapadas.
 *
 * Design intent: keep it dead simple so the admin can add new escapadas
 * without asking me to touch code.
 *
 * Fields:
 *   - Metadata (slug, card_title, card_subtitle, country)
 *   - Full title + tagline
 *   - Hero image (URL) + gallery (URLs, one per line)
 *   - Summary block (destination/flight/duration/ideal_for/highlight)
 *   - Intro + content (markdown-lite; use `## ` for day headers)
 *   - Tips (one per line)
 *   - Opinion
 *   - 4 affiliate CTAs at the bottom (label + provider + href + icon)
 *   - Published toggle
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { escapadasApi, mediaApi, formatApiError } from "./api";
import { ArrowLeft, Plus, Save, Trash2, ExternalLink, Upload, Library, Loader2, X } from "lucide-react";
import ImageSelector from "./ImageSelector";

const EMPTY = {
  slug: "",
  title: "",
  tagline: "",
  card_title: "",
  card_subtitle: "",
  country: "",
  hero_image: "",
  gallery: [],
  summary: { destination: "", flight: "", duration: "", ideal_for: "", highlight: "" },
  intro: "",
  content: "",
  tips: [],
  opinion: "",
  affiliates: [
    { label: "Vuelos", provider: "", href: "", icon: "plane" },
    { label: "Hotel", provider: "", href: "", icon: "bed" },
    { label: "Actividades y entradas", provider: "", href: "", icon: "ticket" },
    { label: "Seguro de viaje", provider: "", href: "", icon: "shield" },
  ],
  published: true,
  order: 0,
};

export default function AdminEscapadas() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null); // full escapada being edited
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await escapadasApi.list();
      setItems(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setSelected({ ...EMPTY });
    setDirty(true);
  };

  const openItem = async (id) => {
    try {
      const doc = await escapadasApi.get(id);
      setSelected({ ...EMPTY, ...doc, summary: { ...EMPTY.summary, ...(doc.summary || {}) } });
      setDirty(false);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const patch = (upd) => {
    setSelected((s) => ({ ...s, ...upd }));
    setDirty(true);
  };

  const save = async () => {
    if (!selected) return;
    if (!selected.title?.trim()) { setError("El título es obligatorio"); return; }
    setSaving(true); setError(null);
    try {
      const payload = { ...selected };
      // Normalise arrays: gallery may be array of {src,caption}; keep as-is
      const saved = selected.id
        ? await escapadasApi.update(selected.id, payload)
        : await escapadasApi.create(payload);
      setSelected({ ...EMPTY, ...saved, summary: { ...EMPTY.summary, ...(saved.summary || {}) } });
      setDirty(false);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected?.id) return;
    if (!window.confirm(`¿Borrar "${selected.card_title || selected.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await escapadasApi.remove(selected.id);
      setSelected(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-[#3A506B] hover:text-[#16335A] text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Volver
            </Link>
            <h1 className="font-serif text-3xl text-[#16335A]">Escapadas</h1>
          </div>
          <button
            onClick={startNew}
            data-testid="new-escapada-btn"
            className="inline-flex items-center gap-2 bg-[#16335A] text-white px-5 py-2.5 rounded-full text-sm hover:bg-[#214A82]"
          >
            <Plus size={16} /> Nueva escapada
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded">{error}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar: list */}
          <aside className="bg-white border border-[#EAE3D5] rounded p-3 h-fit">
            {loading ? (
              <p className="text-[#3A506B] italic text-sm p-3">Cargando…</p>
            ) : items.length === 0 ? (
              <p className="text-[#3A506B] italic text-sm p-3">Sin escapadas todavía.</p>
            ) : (
              <ul className="space-y-1">
                {items.map((it) => (
                  <li key={it.id}>
                    <button
                      onClick={() => openItem(it.id)}
                      data-testid={`escapada-list-item-${it.slug}`}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                        selected?.id === it.id
                          ? "bg-[#16335A]/10 text-[#16335A]"
                          : "hover:bg-[#F3EFE7] text-[#0A192F]"
                      }`}
                    >
                      <div className="font-medium leading-tight">{it.card_title || it.title}</div>
                      <div className="text-[0.7rem] text-[#3A506B] mt-0.5">
                        {it.slug}{" "}
                        {!it.published && <span className="text-amber-700">· borrador</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Editor */}
          <section>
            {!selected ? (
              <div className="bg-white border border-[#EAE3D5] rounded p-10 text-center text-[#3A506B]">
                <p className="italic">Selecciona una escapada o pulsa &ldquo;Nueva escapada&rdquo; para empezar.</p>
              </div>
            ) : (
              <EscapadaForm
                escapada={selected}
                onChange={patch}
                onSave={save}
                onDelete={remove}
                saving={saving}
                dirty={dirty}
              />
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Form
// ─────────────────────────────────────────────────────────────
function EscapadaForm({ escapada: e, onChange, onSave, onDelete, saving, dirty }) {
  const patchSummary = (k, v) => onChange({ summary: { ...e.summary, [k]: v } });
  const patchAff = (i, k, v) => {
    const next = [...e.affiliates];
    next[i] = { ...next[i], [k]: v };
    onChange({ affiliates: next });
  };
  const galleryText = (e.gallery || []).map((g) => (typeof g === "string" ? g : g.src)).join("\n");
  const tipsText = (e.tips || []).join("\n");

  return (
    <div className="bg-white border border-[#EAE3D5] rounded p-6 lg:p-8">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-[#16335A]">
          {e.id ? "Editar escapada" : "Nueva escapada"}
        </h2>
        <div className="flex items-center gap-2">
          {e.id && e.slug && (
            <a
              href={`/escapadas/${e.slug}`}
              target="_blank"
              rel="noreferrer"
              className="text-[#3A506B] hover:text-[#16335A] text-xs inline-flex items-center gap-1.5"
            >
              <ExternalLink size={12} /> Ver pública
            </a>
          )}
          {e.id && (
            <button onClick={onDelete} className="text-red-700 hover:text-red-900 text-xs inline-flex items-center gap-1">
              <Trash2 size={13} /> Borrar
            </button>
          )}
          <button
            onClick={onSave}
            disabled={saving || !dirty}
            data-testid="save-escapada-btn"
            className="inline-flex items-center gap-2 bg-[#E8C97A] text-[#16335A] px-5 py-2 rounded-full text-sm font-medium hover:bg-[#D4B35E] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={14} /> {saving ? "Guardando…" : dirty ? "Guardar" : "Guardado"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Text label="Título largo" value={e.title} onChange={(v) => onChange({ title: v })} placeholder="Fin de semana en Niza, Mónaco y Villefranche-sur-Mer" required />
        <Text label="Slug (URL)" value={e.slug} onChange={(v) => onChange({ slug: v })} placeholder="cote-dazur (auto)" hint="Se genera solo si lo dejas vacío" />
        <Text label="Título de la tarjeta" value={e.card_title} onChange={(v) => onChange({ card_title: v })} placeholder="Côte d'Azur" />
        <Text label="Subtítulo de la tarjeta" value={e.card_subtitle} onChange={(v) => onChange({ card_subtitle: v })} placeholder="Niza · Mónaco · Villefranche-sur-Mer" />
        <Text label="País" value={e.country} onChange={(v) => onChange({ country: v })} placeholder="Francia" />
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-[#0A192F] cursor-pointer">
            <input type="checkbox" checked={e.published} onChange={(ev) => onChange({ published: ev.target.checked })} className="w-4 h-4" />
            Publicada
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-[#0A192F] ml-4">
            Orden
            <input type="number" value={e.order || 0} onChange={(ev) => onChange({ order: parseInt(ev.target.value || "0", 10) })} className="w-16 px-2 py-1 border border-[#EAE3D5] rounded text-sm" />
          </label>
        </div>
      </div>

      <div className="mt-5">
        <Textarea label="Tagline (frase corta bajo el título)" value={e.tagline} onChange={(v) => onChange({ tagline: v })} rows={3} />
      </div>

      {/* Hero image */}
      <div className="mt-5">
        <ImageSelector value={e.hero_image} onChange={(v) => onChange({ hero_image: v })} label="Imagen de portada (hero)" />
      </div>

      {/* Gallery */}
      <div className="mt-5">
        <label className="block text-xs uppercase tracking-wider text-[#3A506B] font-semibold mb-2">Galería de fotos</label>

        {/* Thumbnails preview */}
        {(e.gallery || []).length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-3">
            {(e.gallery || []).map((g, idx) => {
              const src = typeof g === "string" ? g : g.src;
              const abs = src?.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
              return (
                <div key={idx} className="relative group aspect-square bg-[#FDFBF7] border border-[#EAE3D5] overflow-hidden">
                  <img src={abs} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...(e.gallery || [])];
                      next.splice(idx, 1);
                      onChange({ gallery: next });
                    }}
                    className="absolute top-1 right-1 bg-black/70 hover:bg-red-700 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity rounded"
                    title="Quitar de la galería"
                    data-testid={`gallery-remove-${idx}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Prominent upload buttons */}
        <GalleryUploader
          onAdd={(url) => {
            if (!url) return;
            const next = [...(e.gallery || []), { src: url, caption: "" }];
            onChange({ gallery: next });
          }}
        />

        {/* Fallback: manual URLs */}
        <details className="mt-3">
          <summary className="text-xs text-[#3A506B] cursor-pointer hover:text-[#16335A]">Editar URLs manualmente (avanzado)</summary>
          <textarea
            rows={4}
            value={galleryText}
            onChange={(ev) => {
              const urls = ev.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
              onChange({ gallery: urls.map((u) => ({ src: u, caption: "" })) });
            }}
            placeholder="/uploads/mi-foto1.jpg&#10;https://…"
            className="w-full mt-2 px-3 py-2 border border-[#EAE3D5] rounded text-sm font-mono"
          />
        </details>
      </div>

      {/* Summary block */}
      <div className="mt-6 p-4 bg-[#FDFBF7] border border-[#EAE3D5] rounded">
        <p className="text-xs uppercase tracking-wider text-[#3A506B] font-semibold mb-3">Resumen de la escapada</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Text label="Destino" value={e.summary?.destination || ""} onChange={(v) => patchSummary("destination", v)} placeholder="Niza, Mónaco y Villefranche" />
          <Text label="Vuelo / desplazamiento" value={e.summary?.flight || ""} onChange={(v) => patchSummary("flight", v)} placeholder="Barcelona → Niza · 1 h 25 min" />
          <Text label="Duración" value={e.summary?.duration || ""} onChange={(v) => patchSummary("duration", v)} placeholder="2 días · fin de semana" />
          <Text label="Ideal para" value={e.summary?.ideal_for || ""} onChange={(v) => patchSummary("ideal_for", v)} placeholder="Familias, parejas…" />
        </div>
        <div className="mt-3">
          <Text label="Lo más especial" value={e.summary?.highlight || ""} onChange={(v) => patchSummary("highlight", v)} placeholder="Lo más memorable del viaje" />
        </div>
      </div>

      {/* Editorial content */}
      <div className="mt-5">
        <Textarea label="Introducción" value={e.intro} onChange={(v) => onChange({ intro: v })} rows={3} placeholder="Párrafo inicial que va justo antes del contenido del viaje." />
      </div>
      <div className="mt-3">
        <Textarea
          label="Contenido del artículo"
          value={e.content}
          onChange={(v) => onChange({ content: v })}
          rows={16}
          placeholder={`## Día 1 · Título del día\nPárrafo normal…\n\nNos alojamos en el [Hotel Bonito](https://booking.com/link-afiliado).\n\n>> Reserva aquí | https://enlace-afiliado.com\n\n## Día 2 · Otro título\n…`}
          hint={'Formato: "## " abre sección/día · [texto](url) crea un enlace afiliado inline · **negrita** y *cursiva* · ">> texto | url" para CTA destacado.'}
          mono
        />
      </div>
      <div className="mt-3">
        <Textarea label="Consejos (uno por línea)" value={tipsText} onChange={(v) => onChange({ tips: v.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={4} placeholder="Consejo 1&#10;Consejo 2" />
      </div>
      <div className="mt-3">
        <Textarea label="Nuestra opinión" value={e.opinion} onChange={(v) => onChange({ opinion: v })} rows={4} />
      </div>

      {/* Affiliates */}
      <div className="mt-6 p-4 bg-[#FDFBF7] border border-[#EAE3D5] rounded">
        <p className="text-xs uppercase tracking-wider text-[#3A506B] font-semibold mb-3">Planifica tu escapada — Afiliados</p>
        <div className="space-y-3">
          {(e.affiliates || []).map((a, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-3">
                <Text label="Etiqueta" value={a.label} onChange={(v) => patchAff(i, "label", v)} />
              </div>
              <div className="md:col-span-2">
                <Text label="Proveedor" value={a.provider} onChange={(v) => patchAff(i, "provider", v)} />
              </div>
              <div className="md:col-span-5">
                <Text label="URL de afiliado" value={a.href} onChange={(v) => patchAff(i, "href", v)} placeholder="https://…" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-wider text-[#3A506B] font-semibold mb-1">Icono</label>
                <select value={a.icon} onChange={(ev) => patchAff(i, "icon", ev.target.value)} className="w-full px-2 py-2 border border-[#EAE3D5] rounded text-sm">
                  <option value="plane">Avión</option>
                  <option value="bed">Cama</option>
                  <option value="ticket">Entrada</option>
                  <option value="shield">Escudo</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hero image picker modal */}
    </div>
  );
}

// ── Small form primitives ──
function Text({ label, value, onChange, placeholder, hint, required }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#3A506B] font-semibold mb-1">
        {label}{required && <span className="text-red-600 ml-1">*</span>}
      </span>
      <input
        type="text"
        value={value || ""}
        onChange={(ev) => onChange(ev.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-[#EAE3D5] rounded text-sm"
      />
      {hint && <span className="block mt-1 text-[0.7rem] text-[#3A506B]/70 italic">{hint}</span>}
    </label>
  );
}

function Textarea({ label, value, onChange, rows = 3, placeholder, hint, mono }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#3A506B] font-semibold mb-1">{label}</span>
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(ev) => onChange(ev.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border border-[#EAE3D5] rounded text-sm ${mono ? "font-mono" : ""}`}
      />
      {hint && <span className="block mt-1 text-[0.7rem] text-[#3A506B]/70 italic">{hint}</span>}
    </label>
  );
}

// ── Gallery uploader: prominent upload + library buttons ──
function GalleryUploader({ onAdd }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState(null);

  const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];
  const MAX_BYTES = 8 * 1024 * 1024;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (!ALLOWED.includes(file.type)) {
          setError(`"${file.name}": formato no permitido (usa JPG, PNG, WebP o GIF).`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          setError(`"${file.name}": supera los 8 MB.`);
          continue;
        }
        const m = await mediaApi.upload(file);
        const url = m.url || m.external_url || "";
        if (url) onAdd(url);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="gallery-upload-btn"
          className="inline-flex items-center gap-2 bg-[#16335A] hover:bg-[#214A82] text-white px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Subiendo…" : "Subir fotos"}
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          data-testid="gallery-library-btn"
          className="inline-flex items-center gap-2 bg-white hover:bg-[#F3EFE7] text-[#16335A] border border-[#16335A]/20 px-4 py-2.5 rounded-full text-sm font-semibold"
        >
          <Library size={14} /> Elegir de biblioteca
        </button>
        <span className="text-[0.7rem] text-[#3A506B]/70 italic ml-1">JPG, PNG, WebP · máx 8 MB · puedes seleccionar varias a la vez</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED.join(",")}
        multiple
        className="hidden"
        onChange={(ev) => handleFiles(ev.target.files)}
        data-testid="gallery-file-input"
      />

      {error && (
        <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">{error}</p>
      )}

      {pickerOpen && (
        <div className="mt-3">
          <ImageSelector
            value=""
            onChange={(url) => {
              if (url) onAdd(url);
              setPickerOpen(false);
            }}
            label="Selecciona una imagen de la biblioteca y se añadirá a la galería"
          />
          <button
            type="button"
            onClick={() => setPickerOpen(false)}
            className="mt-2 text-xs text-[#3A506B] hover:text-red-700"
          >
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
}
